<template>
  <div class="row question-wrapper" :class="[$config.project]">
    <div :class="{ 'col-12': type === 'in-video' }" class="col col-8 details">
      <div class="container-head">
        <a v-if="$store.state.layout === 'desktop'" class="back-button" @click="backToQuestions">
          <BackArrow />
        </a>
        <a v-else class="back-button" @click="backToQuestions">
          <MobileBackArrows />
        </a>
        <div class="question-actions">
          <div
            :class="{ 'selected': questionInfo.followed }"
            class="follow lms-access"
            data-event="follow_question.click"
            @click="followQuestion(!questionInfo.followed, questionInfo.id)"
          >
            <QaFollowIcon /> {{ questionInfo.followed ? 'Followed' : 'Follow this topic' }}
          </div>
          <div
            v-if="$store.getters['auth/perms'].includes('lms.qa-support') || ['instructor'].includes(nuxt?.layoutName)"
            :class="{ selected: questionInfo.resolved }"
            class="mark-resolved lms-access"
            data-event="resolve_question.click"
            @click="markQuestionAsResolved(!questionInfo.resolved, questionInfo.id)"
          >
            <QaResolve /> {{ questionInfo.resolved ? "Resolved" : "Resolve" }}
          </div>
          <div
            v-if="questionInfo.lectureId && ($store.getters['auth/perms'].includes('admin-legacy.qa') || ['instructor'].includes(nuxt?.layoutName))"
            class="unassign lms-access"
            @click="unassignQuestion(questionInfo.id)"
          >
            <QaUnassignIcon /> Unassign
          </div>
          <div class="share" data-event="share.click" @click="$store.dispatch('modals/open', { name: 'share-question', url: shortUrl })">
            <div><ShareIcon /> Share</div>
          </div>
          <div v-if="!['instructor'].includes(nuxt?.layoutName)" class="report">
            <button class="three-dots" data-event="question_options.click" @click="toggleOptions(questionInfo.id)">
              <EllipsisV />
            </button>
            <div v-if="optionsDropdown.includes(questionInfo.id)" class="dropdown">
              <ul>
                <li
                  v-if="userId === questionInfo.userId || $store.getters['auth/perms'].includes('lms.qa-support')"
                  data-event="question_edit.click"
                >
                  <a @click="editQuestion(questionInfo)"> Edit</a>
                </li>
                <li
                  v-if="userId === questionInfo.userId || $store.getters['auth/perms'].includes('lms.qa-support')"
                  data-event="question_delete.click"
                >
                  <a @click="deleteQuestion(questionInfo.id)"> Delete</a>
                </li>
                <li data-event="question_report.click">
                  <a
                    :class="{ disabled: userId === questionInfo.userId }"
                    @click="reportQuestion"
                  >Report</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="question-info">
        <UserLink
          avatar
          class="left"
          type="question-details"
          data-event="question_user.click"
          :info="questionInfo"
        />
        <div class="badge">
          <UserBadge v-if="questionInfo.userRole === 0 || questionInfo.userRole === 2 || (questionInfo.userPerms && questionInfo.userPerms.includes('lms.qa-support'))" :badge="'Instructor'" />
          <UserBadge v-if="!(questionInfo.userRole === 0 || questionInfo.userRole === 2 || (questionInfo.userPerms && questionInfo.userPerms.includes('lms.qa-support'))) && questionInfo.userPaid && +questionInfo.userLevel >= 8" :badge="'Super learner'" tooltip="This user is a Super Learner. To become a Super Learner, you need to reach Level 8." />
        </div>
        <div class="right">
          <span v-if="$store.state.layout === 'desktop' && questionInfo.dateUpdated && questionInfo.answers && questionInfo.answers.length > 0" class="time last-answered" :title="questionInfo.dateUpdated | time">
            Last answered:<p>{{ questionInfo.dateUpdated | date }}</p>
          </span>
          <span v-if="$store.state.layout === 'desktop'" class="time posted-on" :title="questionInfo.date | time">
            Posted on: <p>{{ questionInfo.date | date }}</p>
          </span>
          <div class="votes">
            <a
              class="lms-access voteUp"
              :class="{ 'vote': questionInfo.upvoted === true }"
              data-event="question_vote.click"
              @click.prevent="vote('questions', questionInfo.id, 'upvote', questionInfo.id)"
            ><QaUpArrowIcon /></a>
            {{ questionInfo.votes }}
          </div>
        </div>
      </div>
      <h1 class="question-title h5">
        <span
          v-if="$store.getters['auth/perms'].includes('lms.qa-support') && reportedSubTitle"
          class="reported"
        >Reported {{ reportedSubTitle }}:</span>
        <span v-else-if="questionInfo.resolved" class="resolved">Resolved:</span>
        <span>{{ questionInfo.title }}</span>
      </h1>
      <div
        v-if="questionInfo.course && questionInfo.lecture && type === 'main'"
        class="question-path"
      >
        in
        <nuxt-link
          class="course-name"
          data-event="question_course.click"
          :title="questionInfo.course"
          :to="$config.project === 'lms' ? `/courses/preview/${questionInfo.courseSlug + '/' || ''}` : `/courses/${questionInfo.courseSlug}/`"
        >
          {{ questionInfo.course }}
        </nuxt-link>
        <span>/</span>
        <component
          :is="$config.project === 'site' ? 'a' : 'nuxt-link'" class="lms-access lesson-name" :title="questionInfo.lecture"
          :to="$config.project === 'site' ? '' : `/courses/${questionInfo.courseSlug}/${questionInfo.lectureSlug}/`"
          data-event="question_lecture.click"
        >
          {{ questionInfo.lecture }}
        </component>
      </div>
      <div class="question-body">
        <div v-html="seoFormat(questionInfo.body, questionInfo.date)" />
      </div>
      <span v-if="$store.state.layout === 'mobile' && questionInfo.dateUpdated && questionInfo.answers && questionInfo.answers.length > 0" class="time last-answered" :title="questionInfo.dateUpdated | time">
        Last answered:<p>{{ questionInfo.dateUpdated | date }}</p>
      </span>
      <span v-if="$store.state.layout === 'mobile'" class="time posted-on" :title="questionInfo.date | time">
        Posted on: <p>{{ questionInfo.date | date }}</p>
      </span>
      <div class="question-answers">
        <div class="count">
          {{ countAnswers }} answers ( {{ countHelpfulAnswers }} marked as helpful)
        </div>
        <div
          v-for="(answer, idx) in questionInfo.answers"
          :id="`answer-${answer.id}`" :key="answer.id" :data-id="answer.id"
          class="answer" :class="{ pinned: answer.pinned, helpful: answer.helpful, highlighted: answer.id === +$route.query.answerId }"
        >
          <button
            v-if="adminOrQaSupport"
            class="pin-answer"
            :class="{ selected: answer.pinned }"
            @click="pinAnswer(!answer.pinned, answer.id)"
          >
            <PinIcon />
          </button>
          <div class="answer-info">
            <UserLink
              avatar
              class="left"
              type="question-details"
              data-event="answer_user.click"
              :info="answer"
            />
            <div class="badge">
              <UserBadge v-if="answer.userRole === 0 || answer.userRole === 2 || (answer.userPerms && answer.userPerms.includes('lms.qa-support'))" :badge="'Instructor'" />
              <UserBadge v-if="!(answer.userRole === 0 || answer.userRole === 2 || (answer.userPerms && answer.userPerms.includes('lms.qa-support'))) && answer.userPaid && +answer.userLevel >= 8" :badge="'Super learner'" tooltip="This user is a Super Learner. To become a Super Learner, you need to reach Level 8." />
            </div>
            <div class="right">
              <span v-if="$store.state.layout === 'desktop'" class="time posted-on" :title="answer.date | time">
                Posted on: <p>{{ answer.date | date }}</p>
              </span>
              <div class="votes">
                <a
                  class="voteUp lms-access"
                  :class="{ 'vote': answer.upvoted }"
                  data-event="answer_vote.click"
                  @click.prevent="vote('answers', answer.id, 'upvote', idx)"
                ><QaUpArrowIcon /></a>
                {{ answer.votes }}
              </div>
            </div>
          </div>
          <div ref="answers" class="answer-body">
            <div v-html="seoFormat(answer.body, answer.date)" />
          </div>
          <div class="answer-footer">
            <span v-if="$store.state.layout === 'mobile'" class="time posted-on" :title="answer.date | time">
              Posted on: <p>{{ answer.date | date }}</p>
            </span>
            <div
              v-if="$store.getters['auth/perms'].includes('lms.qa-support') && answer.reported"
              class="reported-answer"
            >
              <CloseIcon />
              Reported
            </div>
            <div
              v-if="$store.state.layout === 'desktop'"
              class="mark-helpful lms-access"
              v-on="userId === questionInfo.userId || adminOrQaSupport ? { click: () => markHelpfulAnswer(!answer.helpful, answer.id) } : {}"
            >
              <QaMarkIcon v-if="answer.helpful || userId === questionInfo.userId || adminOrQaSupport" />
              {{ answer.helpful ? "Helpful answer" : userId === questionInfo.userId || adminOrQaSupport ? "Mark as helpful" : '' }}
            </div>
            <Tooltip v-if="userId === questionInfo.userId || adminOrQaSupport" class="mark-as-helpful-tooltip">
              If you find this answer well-researched and useful, please reward the member who replied and help the community notice it by marking it as helpful.
            </Tooltip>
            <div v-if="$store.state.layout === 'mobile'" class="mark-helpful">
              {{ answer.helpful ? "Helpful answer" : '' }}
            </div>
            <div class="report" :class="{ 'report-success': userId === questionInfo.userId }">
              <button class="three-dots" data-event="answer_options.click" @click="toggleOptions(answer.id)">
                <EllipsisV />
              </button>
              <div v-if="optionsDropdown.includes(answer.id)" class="dropdown">
                <ul>
                  <li v-if="$store.state.layout === 'mobile' && userId && (userId === questionInfo.userId || adminOrQaSupport || answer.helpful)">
                    <a
                      :class="{ helpful: answer.helpful }"
                      v-on="userId === questionInfo.userId || adminOrQaSupport ? { click: () => markHelpfulAnswer(!answer.helpful, answer.id) } : {}"
                    >
                      {{ answer.helpful ? "Helpful answer" : userId === questionInfo.userId || adminOrQaSupport ? "Mark as helpful" : '' }}
                    </a>
                  </li>
                  <li v-if="userId === answer.userId || $store.getters['auth/perms'].includes('lms.qa-support')">
                    <a @click="editAnswer(answer.id, answer.body)">Edit</a>
                  </li>
                  <li v-if="userId === answer.userId || $store.getters['auth/perms'].includes('lms.qa-support')">
                    <a @click="deleteAnswer(answer.id)">Delete</a>
                  </li>
                  <li>
                    <a
                      :class="{ disabled: userId === answer.userId }"
                      @click="$config.project === 'site' ? $router.push('/signup/') : $store.dispatch('modals/open', { name: 'report-user', place: 'qa-hub', slug: answer.userSlug, entityId: answer.id, entityType: 'answer' })"
                    >Report</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div :class="{ disabled: replySubmitting }" class="submit-answer">
        <h2 class="h5">
          Submit an answer
        </h2>
        <div class="custom-input-group">
          <client-only>
            <Editor
              v-model="content"
              :type="type"
              :class="{ invalid: error }"
              data-event="answer_editor.click"
              placeholder="Click here to reply..."
            />
          </client-only>
          <div class="btn-holder">
            <button class="btn btn-primary" data-event="answer_submit.click" @click="submitReply">
              {{ submitButtonTitle }}
            </button>
            <button
              v-if="content"
              class="btn-text btn-cancel"
              data-event="answer_cancel.click"
              @click="cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="col col-4">
      <PromoBanner v-if="showPromoBanner && type === 'main'" :is-campaign="showPromoBanner" position="side" />
      <ABBanner
        v-else-if="abTest && type === 'main' && $config.project === 'site'"
        first="GET"
        second="World-Class"
        :title="$config.platform === 'ds' ? 'Data Science' : 'FINANCE COURSES'"
        :third="$config.platform === 'ds' ? 'Courses' : ''"
        side-banner
        place="qa-hub"
      />
      <SimilarQuestions
        v-if="type === 'main'"
        type="related" :related-questions="questionInfo.related"
        :question="questionInfo.id"
      />
    </div>
  </div>
</template>

<script>
import Prism from 'prismjs';
import ABBanner from '../ABBanner';
import PromoBanner from '../PromoBanner';
import { CAMPAIGNS_WITH_SIDE_BANNERS } from '../utils/campaigns';
import jsonldDS from '../utils/json-ld-ds';
import jsonldFA from '../utils/json-ld-fa';
import global from '../mixins/global';
import QaDownArrowIcon from '../assets/svg/icon-down-arrow.svg?vue-component';
import QaUpArrowIcon from '../assets/svg/icon-up-arrow.svg?vue-component';
import QaResolve from '../assets/svg/icon-qa-resolve.svg?vue-component';
import QaMarkIcon from '../assets/svg/icon-qa-mark.svg?vue-component';
import QaFollowIcon from '../assets/svg/icon-qa-follow.svg?vue-component';
import QaUnassignIcon from '../assets/svg/icon-qa-unassign.svg?vue-component';
import PinIcon from '../assets/svg/icon-pin.svg?vue-component';
import EllipsisV from '../assets/svg/icon-qa-ellipsis-v.svg?vue-component';
import BackArrow from '../assets/svg/icon-arrow-back.svg?vue-component';
import MobileBackArrows from '../assets/svg/icon-arrow-left.svg?vue-component';
import ShareIcon from '../assets/svg/icon-share.svg?vue-component';
import CloseIcon from '../assets/svg/icon-close.svg?vue-component';
import Modal from '../Modal';
import SimilarQuestions from './SimilarQuestions';
import UserLink from './UserLink';
import UserBadge from '../UserBadge';
import Tooltip from '../components/Tooltip';
import 'prismjs/themes/prism.css';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-r';

export default {
  filters: {
    date(v) {
      return new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    time(v) {
      return new Date(v).toLocaleTimeString('en-GB');
    },
  },
  components: {
    Editor: async () => import('./Editor'),
    /* eslint-disable no-use-before-define */
    QaDownArrowIcon,
    QaUpArrowIcon,
    QaMarkIcon,
    QaResolve,
    QaFollowIcon,
    QaUnassignIcon,
    BackArrow,
    MobileBackArrows,
    EllipsisV,
    ShareIcon,
    CloseIcon,
    Modal,
    SimilarQuestions,
    UserLink,
    UserBadge,
    Tooltip,
    PinIcon,
    ABBanner,
    PromoBanner,
  },
  mixins: [global],
  layout: 'user',
  props: {
    questionInfo: { type: Object, default: () => {}, required: true },
    countAnswers: { type: Number, required: true },
    countHelpfulAnswers: { type: Number, required: true },
    type: { type: [String], default: '' },
  },
  data() {
    return {
      replySubmitting: false,
      content: '',
      optionsDropdown: [],
      editMode: false,
      editAnswerId: 0,
      showEditor: false,
      reportedSubTitle: '',
      error: false,
    };
  },
  head() {
    const jsonld = this.$config.platform === 'ds' ? jsonldDS : jsonldFA;
    let output = {};

    if (this.type !== 'in-video') {
      const metaDescription = `Find professional answers about "${this.questionInfo.title}" in 365 Data Science's Q&A Hub. Join today!`;

      const seoWebPage = {
        ...jsonld.WebPage,
        url: `${this.$config.siteUrl}${this.$route.fullPath}`,
        name: this.questionInfo.title,
        description: metaDescription,
        datePublished: this.questionInfo.date,
        dateModified: this.questionInfo.dateUpdated,
      };

      const seoQAPage = {
        ...jsonld.QAPage,
        mainEntity: {
          ...jsonld.QAPage.mainEntity,
          name: this.questionInfo.title,
          text: this.removeTags(this.questionInfo.body),
          answerCount: this.countAnswers,
          upvoteCount: this.questionInfo.votes,
          datePublished: this.questionInfo.date,
          author: {
            ...jsonld.QAPage.mainEntity.author,
            name: this.questionInfo.user,
            url: `${this.$config.lmsUrl}/profile/${this.questionInfo.userSlug}/`,
          },
          suggestedAnswer: [],
        },
      };

      const votedAnswers = [...this.questionInfo.answers].filter((a) => a.votes > 0);
      const haveHelpful = !![...this.questionInfo.answers].filter((a) => a.helpful).length;
      let mostVotedAnswerId = {};

      if (votedAnswers.length) {
        mostVotedAnswerId = votedAnswers.sort((a, b) => a.votes - b.votes)[votedAnswers.length - 1].id;
      }

      this.questionInfo.answers.forEach((a) => {
        let userUrl = `${this.$config.lmsUrl}/profile/${this.questionInfo.userSlug}/`;
        if (a.userRole === 2) {
          userUrl = `${this.$config.siteUrl}/instructors/${a.userSlug}/`;
        }

        const answer = {
          '@type': 'Answer',
          datePublished: a.date,
          text: this.removeTags(a.body),
          upvoteCount: a.votes,
          url: `${this.$config.siteUrl}/question/${this.questionInfo.url}/#answer-${a.id}`,
          author: {
            type: 'Person',
            name: a.user,
            url: userUrl,
          },
        };
        if (a.helpful || (!haveHelpful && mostVotedAnswerId === a.id)) {
          seoQAPage.mainEntity.acceptedAnswer = answer;
        } else {
          seoQAPage.mainEntity.suggestedAnswer.push(answer);
        }
      });

      const link = [];
      if (this.canonicalURL) {
        link.push({ rel: 'canonical', href: this.canonicalURL() });
      }

      // set correct metaTitle using auto generated number in slug
      let metaTitle = `${this.questionInfo.title}`;
      const regex = /.+-(\d+)$/;
      const urlNumber = regex.exec(this.questionInfo.url);

      if (urlNumber && +urlNumber[1] > 0) {
        metaTitle += ` ${+urlNumber[1]}`;
      }
      metaTitle += ' – Q&A Hub';

      output = {
        titleTemplate:
        this.questionInfo && this.type === 'main'
          ? `${metaTitle}%s`
          : `${this.questionInfo.course}%s`,
        meta: this.$config.project === 'site' ? this.metaProps({
          fullPath: `${this.$config.siteUrl}${this.$route.fullPath}`,
          metaTitle,
          metaDescription,
        }) : '',
        link,
        script: [
          {
            vmid: 'jsonld',
            type: 'application/ld+json',
            json: [
              {
                '@context': 'https://schema.org',
                '@graph': [jsonld.WebSite, seoWebPage, seoQAPage],
              },
              {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [{
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Q&A',
                  item: `${this.$config.siteUrl}/qa-hub/`,
                }, {
                  '@type': 'ListItem',
                  position: 2,
                  name: metaTitle,
                }],
              },
            ],
          },
        ],
      };
    }

    return output;
  },
  computed: {
    shortUrl() {
      return this.questionInfo.shortLink
        ? `${this.$config.siteUrl}/q/${this.questionInfo.shortLink}`
        : `${this.$config.siteUrl}/question/${this.questionInfo.url}/`;
    },
    submitButtonTitle() {
      let title;
      if (this.editMode) {
        title = 'Edit your answer';
      } else {
        title = 'Submit answer';
      }
      if (this.replySubmitting) {
        title = 'Submitting...';
      }
      return title;
    },
    userId() {
      return this.$store.getters['auth/user'].id;
    },
    adminOrQaSupport() {
      return this.$store.getters['auth/perms'].includes('lms.qa-support') || this.$store.getters['auth/user'].role === 'admin';
    },
    abTest() {
      // return this.$store.state.tests?.tests?.['q-and-a-banner'] === 1;
      return true;
    },
    showPromoBanner() {
      if (this.$store.state.campaign && this.$store.state.campaign.startsWith('free-') && this.$store.getters['banners/showFreeBannerToUser']) {
        return true;
      }

      if (this.$store.getters['auth/user'].activeSubscription && this.$store.state.campaign === 'cyber-monday-lifetime-2024'
        && this.$store.state.banners.active && this.$store.state.banners.active.plan === 3) {
        return false;
      }

      const isCampaign = CAMPAIGNS_WITH_SIDE_BANNERS.includes(this.$store.state.campaign);
      return isCampaign && this.$store.getters['banners/showPromoBannerToUser'];
    },
    nuxt() {
      if (this.$nuxt) {
        return this.$nuxt;
      }

      return {};
    },
  },
  watch: {
    content(v) {
      this.$store.commit('qa-hub/setAnswerBody', v);
    },
  },
  mounted() {
    setTimeout(() => {
      if (this.questionInfo.answers && this.questionInfo && this.questionInfo.answers.find((answer) => answer.reported) && this.questionInfo.answers.find((answer) => answer.reported).reported && this.questionInfo.reported) {
        this.reportedSubTitle = 'Q&A';
      } else if (this.questionInfo && this.questionInfo.reported) {
        this.reportedSubTitle = 'Q';
      } else if (this.questionInfo.answers && this.questionInfo.answers.find((answer) => answer.reported) && this.questionInfo.answers.find((answer) => answer.reported).reported) {
        this.reportedSubTitle = 'A';
      }
    }, 500);

    this.$root.$on('emit-click', (e) => {
      if (!e.target.closest('.report') || e.target.classList.contains('report')) {
        this.optionsDropdown = [];
      }
    });

    const unsavedChanges = (e) => {
      if (!this.$store.state['qa-hub'].answerBody || (this.$store.state['qa-hub'].edited && !this.$store.state['qa-hub'].formSubmitting)) {
        return undefined;
      }
      e.preventDefault();
      const confirmationMessage = 'Changes you made may not be saved.';
      // eslint-disable-next-line no-param-reassign
      (e || window.event).returnValue = confirmationMessage;
      return confirmationMessage;
    };

    window.addEventListener('beforeunload', unsavedChanges);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('beforeunload', unsavedChanges));

    document.querySelectorAll('pre code').forEach((block) => {
      const observer = new MutationObserver(() => {
        if (!block.querySelector('span')) {
          Prism.highlightElement(block);
          observer.disconnect();
        }
      });

      observer.observe(block, { childList: true, subtree: true });
    });

    this.loadGallery(this.$el.querySelectorAll('.question-body > div'));

    if (this.$refs.answers && this.$refs.answers.length) {
      this.$refs.answers.forEach((answers) => {
        this.loadGallery(answers.querySelectorAll('div'));
      });
    }

    if (this.$route.query.answerId) {
      setTimeout(() => {
        const answer = this.$el.querySelector(`.answer[data-id="${this.$route.query.answerId}"]`);

        if (answer) {
          answer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  },
  methods: {
    async pinAnswer(pin, answerId) {
      const answer = this.questionInfo.answers.find((a) => a.id === answerId);
      this.$set(answer, 'pinned', pin);
      const action = pin ? 'pin' : 'unpin';
      await this.$axios.$patch(`/qa-hub/answers/${answerId}/${action}`);
      this.$emit('refreshquestion', this.questionInfo.url);
    },
    async unassignQuestion(id) {
      await this.$axios.$patch(`/qa-hub/questions/${id}/lecture`);
      await this.$store.dispatch('modals/open', {
        name: 'default',
        title: 'Saved!',
        message: 'Question unassigned successfully from lesson.',
      });
      this.$emit('refreshquestion', this.questionInfo.url);
    },
    async vote(subject, id, type, idx) {
      const { votes } = await this.$axios.$patch(`/qa-hub/${subject}/${id}/vote`, {
        type,
      }, { progress: false });
      this.$emit('voted', {
        subject, id, type, votes: +votes, idx,
      });
      if (subject === 'questions') {
        this.$set(this.questionInfo, 'upvoted', !this.questionInfo.upvoted);
      }
      if (subject === 'answers') {
        const answer = this.questionInfo.answers[idx];
        this.$set(answer, 'upvoted', !answer.upvoted);
      }
    },
    async deleteQuestion(questionId) {
      const confirm = await this.$store.dispatch('modals/open', {
        name: 'confirm',
        title: 'Are you sure you want to delete this question?',
        cancelBtnClass: 'btn-primary',
        confirmBtnClass: 'btn-danger-light',
        reverseButtonsOrder: true,
        eventPrefix: 'delete_question_modal',
      });
      if (confirm) {
        const res = await this.$axios.$delete(`/qa-hub/questions/${questionId}`);
        await this.$store.dispatch('modals/open', {
          name: 'default',
          title: 'Question successfully deleted!',
        });

        this.$store.commit('qa-hub/setAnswerBody', null);

        this.$tracking.analytics().track('qa_question_deleted', {
          qa_question_id: this.questionInfo.id,
          course_id: this.questionInfo.courseId,
          lecture_id: this.questionInfo.lectureId,
          origin: this.type === 'main' ? 'q&a hub' : 'lecture',
          sequence_source: res.sequenceSource,
          question_text: this.questionInfo.body,
        });

        if (this.type === 'in-video') {
          this.$root.$emit('back-to-questions');
        } else {
          this.$router.push('/qa-hub/');
        }
      }
    },
    async reportQuestion() {
      if (this.$config.project === 'site') {
        this.$router.push('/signup/');
      } else {
        this.$store.dispatch('modals/open', {
          name: 'report-user',
          place: 'qa-hub',
          slug: this.questionInfo.userSlug,
          entityId: this.questionInfo.id,
          entityType: 'question',
          eventPrefix: 'report_question_modal',
        });
      }
    },
    editQuestion(questionInfo) {
      this.$emit('editquestion', {
        title: questionInfo.title,
        body: questionInfo.body,
        url: questionInfo.url,
        id: questionInfo.id,
        courseSlug: questionInfo.courseSlug,
        courseId: questionInfo.courseId,
      });
    },
    followQuestion(follow, questionId) {
      setTimeout(async () => {
        await this.$axios.$post(
          `/qa-hub/questions/${questionId}/${follow ? 'follow' : 'unfollow'}`,
        );
        this.$emit('refreshquestion', this.questionInfo.url);
      }, 510);
    },
    async markQuestionAsResolved(resolved, questionId) {
      const confirm = await this.$store.dispatch('modals/open', {
        name: 'confirm',
        title: 'Are you sure?',
        message: `This action will mark the question as ${
          resolved ? 'resolved' : 'unresolved'
        }.`,
      });

      if (!confirm) {
        return;
      }
      await this.$axios.$patch(`/qa-hub/questions/${questionId}/resolved`, {
        resolved,
      });
      this.$emit('refreshquestion', this.questionInfo.url);
    },
    async markHelpfulAnswer(helpful, answerId) {
      if (helpful) {
        const res = await this.$axios.$patch(`/qa-hub/answers/${answerId}/helpful`);

        this.$tracking.analytics().track('qa_question_resolved', {
          qa_question_id: this.questionInfo.id,
          course_id: this.questionInfo.courseId,
          lecture_id: this.questionInfo.lectureId,
          origin: this.type === 'main' ? 'q&a hub' : 'lecture',
          sequence_source: res.sequenceSource,
          question_text: this.questionInfo.body,
          helpful_answer_id: answerId,
        });
      } else {
        await this.$axios.$delete(`/qa-hub/answers/${answerId}/helpful`);
      }
      this.$emit('refreshquestion', this.questionInfo.url);
    },
    editAnswer(answerId, answerBody) {
      setTimeout(() => {
        const container = this.$el.querySelector('.submit-answer');
        container.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 150);
      this.$root.$emit('set-editor-value', answerBody);
      this.editMode = true;
      this.editAnswerId = answerId;
      this.content = answerBody;
      const editor = document.querySelector('.qa-editor');
      this.$root.$emit('expand', editor);
      this.optionsDropdown = [];
    },
    async deleteAnswer(answerId) {
      const confirm = await this.$store.dispatch('modals/open', {
        name: 'confirm',
        title: 'Are you sure you want to delete this answer?',
        message: 'Deleting it will remove all the content inside it.',
        cancelBtnClass: 'btn-primary',
        confirmBtnClass: 'btn-danger-light',
        reverseButtonsOrder: true,
      });
      if (!confirm) {
        return false;
      }
      await this.$axios.$delete(`/qa-hub/answers/${answerId}`);
      await this.$store.dispatch('modals/open', {
        name: 'default',
        title: 'Answer successfully deleted!',
      });

      const answer = this.questionInfo.answers.find((a) => a.id === answerId);

      this.$tracking.analytics().track('qa_answer_deleted', {
        qa_answer_id: answerId,
        qa_question_id: this.questionInfo.id,
        course_id: this.questionInfo.courseId,
        lecture_id: this.questionInfo.lectureId,
        answer_text: answer.body,
      });

      this.$emit('refreshquestion', this.questionInfo.url);
      return true;
    },
    toggleOptions(id) {
      if (!this.optionsDropdown.includes(id)) {
        this.optionsDropdown = [];
        this.optionsDropdown.push(id);
      } else {
        this.optionsDropdown = [];
      }
    },
    backToQuestions() {
      if (this.$store.state['qa-hub'].answerBody) {
        // eslint-disable-next-line no-alert
        const leave = window.confirm('Leave page? Changes you made may not be saved.');
        if (!leave) {
          return;
        }
        this.$store.commit('qa-hub/setAnswerBody', null);
      }
      if (this.type === 'in-video' || ['instructor'].includes(this.nuxt?.layoutName)) {
        this.$root.$emit('back-to-questions');
      } else {
        window.history.back();
      }
    },
    seoFormat(html, date) {
      const dateAdded = new Date(date);
      const now = new Date(Date.now() - 20000);

      let newHTML = html;
      const regExp = /<a [^>]+>([^<]+)<\/a>/g;
      if (regExp.test(html)) {
        html.match(regExp).forEach((el) => {
          let newEl = el;
          newEl = newEl.replace('rel="noopener noreferrer nofollow"', '');
          newEl = newEl.replace('target="_blank"', '');
          if (!(/365datascience.com/).test(el)) {
            newEl = newEl.replace('<a', '<a rel="ugc noopener noreferrer nofollow" target="_blank"');
          }
          newHTML = newHTML.replace(el, newEl);
        });
      }

      if (dateAdded.getTime() < now.getTime()) {
        newHTML = newHTML.replace(/(src="https:\/\/.+?\/resources\/.+?\/)(.+?)\.(.+?)"/g, (m0, m1, m2, m3) => this.$convertImageToWebpThumb(1170, m1, m2, m3));
      }

      return newHTML;
    },
    async submitReply() {
      if (!this.$store.state.auth.user) {
        this.$router.push('/signup/');
        return;
      }

      try {
        this.replySubmitting = true;
        this.error = false;
        let body = this.$store.state['qa-hub'].answerBody;
        body = this.content.replace(/<p><\/p>/g, '<br>');
        let content = body;
        setTimeout(() => {
          body = '';
          this.content = '';
        }, 100);
        let res;
        if (this.editMode) {
          res = await this.$axios.$put(`/qa-hub/answers/${this.editAnswerId}?ncap=1${this.$store.getters['auth/user'].role === 'instructor' ? '&qa-question=instructor' : ''}`, { body }, { noError: true }).catch((e) => e.response.data);
        } else {
          res = await this.$axios.$post(`/qa-hub/questions/${this.questionInfo.id}/answers?ncap=1${this.$store.getters['auth/user'].role === 'instructor' ? '&qa-question=instructor' : ''}`, { body }, { noError: true }).catch((e) => e.response.data);
        }

        if (res && res.error) {
          if (res.error === 'answerBodyRequired') {
            this.error = true;
            this.replySubmitting = false;
          }

          return;
        }
        dataLayer.push({ event: 'QAanswer' });

        this.$tracking.analytics().track('qa_answer_posted', {
          qa_answer_id: res.id,
          qa_question_id: this.questionInfo.id,
          lecture_id: this.questionInfo.lectureId,
          answer_text: content,
        });

        this.$emit('refreshquestion', this.questionInfo.url);
      } catch (e) {
        this.replySubmitting = false;
      }
      setTimeout(() => {
        this.replySubmitting = false;
      }, 1900);
    },
    cancel() {
      this.editMode = false;
      this.content = '';
      const editor = document.querySelector('.qa-editor');
      editor.classList.remove('expanded');
    },
  },
};
</script>

<style lang="scss">
.question-wrapper {
  padding-top: var(--spacing-md);

  &.site {
    padding-top: var(--spacing-120);
    padding-bottom: var(--spacing-80);
  }

  code:not([class*="language-"]) {
    background: #F5F2F0;
    border: 1px solid #D0CFCE;
    padding: 3px;
    border-radius: 5px !important;
    color: var(--main-red);
    display: inline;
    min-width: 30px;
    vertical-align: middle;
  }

  .details {
    .report {
      position: relative;

      .three-dots {
        width: 15px;
        text-align: right;
        margin-left: -12px;
      }

      button {
        white-space: nowrap;
        border: 0;
        background: none;
        cursor: pointer;
        appearance: none;
        padding: 0;

        svg {
          vertical-align: unset;

          circle[fill] {
            fill: var(--main-light-shade-4);
          }
        }

        &:hover {
          svg circle[fill] {
            fill: var(--main-dark-light-4);
          }
        }

        &:active {
          svg circle[fill] {
            fill: var(--main-dark-light-4);
          }
        }
      }

      .dropdown {
        position: absolute;
        right: 0;
        top: 30px;
        width: 186px;
        background: var(--main-light);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        z-index: 1;

        ul {
          padding: 0;
          margin: 0;
          list-style: none;

          li {
            margin: 0;
            text-align: left;

            a {
              padding: 12px;
              padding-left: var(--spacing-32);
              line-height: 16px;
              display: block;
              cursor: pointer;
              font-size: var(--fs-base);

              &:not(:active):hover {
                background-color: var(--accent-light-1);
              }

              &:active {
                color: var(--accent);
              }

              &.disabled {
                pointer-events: none;
                opacity: 0.6;
              }
            }
          }
        }
      }
    }

    svg {
      vertical-align: middle;
    }

    .container-head {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .back-button {
        cursor: pointer;
        width: 26px;
        text-align: left;
        display: inline-block;
      }

      .question-actions {
        display: flex;
        align-items: center;
        font-weight: 500;
        font-size: 14px;

        .follow,
        .mark-resolved,
        .share div {
          display: flex;
          align-items: center;

          & svg {
            margin-right: var(--spacing-8);
          }
        }

        .share {
          position: relative;
          color: var(--main-dark-light-4);
          cursor: pointer;
          margin-right: 24px;
          font-weight: 500;

          svg path {
            fill: var(--main-dark-light-4);
          }

          & > div:first-of-type:hover {
            color: var(--accent);

            svg path {
              fill: var(--accent);
            }
          }

          & > div:first-of-type:active {
            color: var(--main-dark-light-4);

            svg path {
              fill: var(--main-dark-light-4);
            }
          }

          .share-window {
            right: 0;
            z-index: 1;
            background: var(--main-light);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            position: absolute;
            width: auto;
            padding: var(--spacing-sm);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            a {
              color: var(--brand-alt) !important;
              font-size: 16px;
              word-break: break-word;
            }

            button {
              border: 0;
              background: none;
              font-size: 14px;
              line-height: 23px;
              color: var(--black);
              display: block;
              padding: 0;
              margin: 0;
              cursor: pointer;
            }
          }
        }

        .mark-resolved {
          color: var(--main-dark-light-4);
          cursor: pointer;
          margin-right: 24px;
          font-weight: 500;

          svg circle {
            stroke: var(--main-dark-light-4);
          }

          svg path {
            fill: var(--main-dark-light-4);
          }

          &.selected {
            color: var(--accent);

            svg circle {
              stroke: var(--accent);
              fill: var(--accent);
            }

            svg path {
              stroke: var(--main-light);
              fill: var(--main-light);
            }

            &:hover {
              color: var(--main-dark-light-4);

              svg circle {
                stroke: var(--main-dark-light-4);
                fill: var(--main-light);
              }

              svg path {
                fill: var(--main-dark-light-4);
              }
            }
          }

          &:hover {
            color: var(--accent);

            svg circle {
              stroke: var(--accent);
            }

            svg path {
              fill: var(--accent);
            }
          }
        }

        .unassign {
          color: var(--main-dark-light-4);
          cursor: pointer;
          margin-right: 24px;
          font-weight: 500;

          svg * {
            stroke: var(--main-dark-light-4);
          }

          &:hover {
            color: var(--accent);

            svg path {
              stroke: var(--accent);
            }

            svg circle {
              stroke: var(--accent);
              fill-opacity: 0.3;
            }
          }
        }

        .follow {
          cursor: pointer;
          margin-right: 24px;
          color: var(--main-dark-light-4);

          svg path {
            stroke: var(--main-dark-light-4);
          }

          &:hover {
            color: var(--accent);

            svg path {
              fill: none;
              stroke: var(--accent);
            }
          }

          &.selected {
            color: var(--accent-shade-2);

            svg path {
              fill: var(--accent-shade-2);
              stroke: var(--accent-shade-2);
            }

            &:hover {
              color: var(--accent);

              svg path {
                fill: none;
                stroke: var(--accent);
              }
            }
          }
        }
      }
    }

    .question-title {
      line-height: 140%;
      color: var(--main-dark-shade-0);
      margin: 0;

      .resolved {
        color: var(--main-green-shade-1);
        font-weight: 600;
      }

      .reported {
        color: var(--error);
      }
    }

    .question-path {
      width: 100%;
      overflow: hidden;
      display: flex;
      color: var(--main-dark-shade-0);
      margin: var(--spacing-sm) 0;
      margin-bottom: var(--spacing-16);

      span {
        margin: 0 4px;
      }

      a {
        color: var(--accent-shade-2);
        max-width: 50%;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        cursor: pointer;
        text-decoration: underline;

        &:hover {
          text-decoration: underline;
        }

        &:first-of-type {
          margin-left: 4px;
        }
      }
    }

    .question-body {
      margin: 0 0 var(--spacing-4) 0;
      line-height: 27px;
      color: var(--main-dark-shade-0);
      overflow: auto;

      .external-anchor {
        color: var(--accent);
      }

      img {
        max-width: 100%;
        visibility: visible !important;
      }

      p {
        color: var(--main-dark-light-3) !important;
        margin: 0;
        word-break: break-word;
      }

      pre {
        white-space: break-spaces;
        overflow: auto;

        code {
          white-space: pre-wrap;
        }
      }

      a {
        word-break: break-word;
      }

      & * {
        white-space: normal;
      }

      div:empty {
        &:after {
          content: "\00a0";
          opacity: 0;
        }
      }
    }

    .question-info,
    .answer-info {
      display: flex;
      align-items: center;
      // justify-content: space-between;
      margin: var(--spacing-md) 0 var(--spacing-sm) 0;
      position: relative;

      div.left,
      div.right {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 14px;
      }

      div.right {
        color: var(--main-light-shade-4);
        font-size: 13px;
        flex: 0 0 auto;
        margin-left: auto;

        .time {
          display: flex;
          align-items: center;
          margin-right: 12px;

          p {
            margin: 0 0 0 4px;
            font-size: 13px;
          }
        }

        .votes {
          font-size: 16px;
          font-weight: 600;
          color: var(--main-dark-light-4);

          a {
            cursor: pointer;

            svg {
              transform: scale(1.2);
              vertical-align: unset;
              margin-right: 8px;

              path[fill] {
                fill: var(--main-light-shade-3);
              }

              &:hover {
                path[fill] {
                  fill: var(--main-light-shade-4);
                }
              }
            }

            &.vote {
              svg {
                path[fill] {
                  fill: var(--accent);
                }
              }
            }
          }
        }
      }

      &.answer-info {
        margin: 0 0 var(--spacing-sm) 0;
      }
    }

    .mark-helpful {
      color: var(--main-light-shade-4);
      cursor: pointer;
      margin-right: var(--spacing-xs);
      font-weight: 500;
      font-size: 14px;

      &:hover {
        color: var(--accent);

        svg path {
          fill: var(--accent);
        }
      }

      &:hover ~ .mark-as-helpful-tooltip {
        display: block;
        left: auto;
        transform: none;
      }
    }

    .answer-footer {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      .reported-answer {
        color: var(--error);
        margin-right: var(--spacing-sm);

        svg {
          width: 12px;
          vertical-align: unset;

          path {
            stroke: var(--error);
          }
        }
      }
    }

    .question-answers {
      margin-bottom: var(--spacing-60);

      .count {
        font-weight: 500;
        font-size: 14px;
        color: var(--main-dark-shade-0);
        border-bottom: 1px solid var(--lighter-gray);
        padding-bottom: var(--spacing-sm);
        margin-bottom: 20px;
        margin-top: var(--spacing-20);
      }

      .answer {
        background: var(--main-light);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        padding: 20px;
        margin-bottom: var(--spacing-sm);
        margin-left: var(--spacing-24);
        position: relative;

        &.votes {
          .answer-info .right .votes .voteUp svg path[fill] {
            fill: var(--accent);
          }
        }

        &.highlighted {
          outline: 2px solid var(--accent);
          transition: all var(--duration) ease;
          animation: border-fade-out 3000ms ease forwards;
          animation-delay: 3000ms;
        }

        @keyframes border-fade-out {
          0% {}
          100% {
            outline-color: transparent;
          }
        }

        .pin-answer {
          height: 25px;
          width: 25px;
          border: 0;
          outline: 0;
          appearance: none;
          background: var(--main-light);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
          border-radius: 50%;
          position: absolute;
          top: -6px;
          left: -16px;

          svg path[stroke] {
            stroke: var(--main-light-shade-4);
          }

          &:hover {
            cursor: pointer;

            svg path[stroke] {
              stroke: var(--accent);
            }
          }

          &.selected {
            svg path[fill] {
              fill: var(--accent);
            }

            svg path[stroke] {
              stroke: var(--accent);
            }

            &:hover {
              svg path[fill] {
                fill: transparent !important;
              }

              svg path[stroke] {
                stroke: var(--main-light-shade-4) !important;
              }
            }
          }
        }

        .answer-body {
          margin: 12px 0;

          p {
            margin: 0;
            word-break: break-word;
          }

          pre {
            white-space: break-spaces;
            overflow: auto;

            code {
              white-space: pre-wrap;
            }
          }

          a {
            word-break: break-word;
          }

          & * {
            white-space: normal;
          }

          code {
            white-space: pre-line;
            border-radius: 2px;
          }

          img {
            max-width: 100%;
            visibility: visible !important;
          }

          div:empty {
            &:after {
              content: "\00a0";
              opacity: 0;
            }
          }
        }

        &:last-of-type {
          margin-bottom: 0;
        }

        &.helpful {
          background: var(--main-green-light-1);

          .mark-helpful {
            color: var(--main-green-shade-1);

            svg path {
              fill: var(--main-green-shade-1);
            }
          }
        }

        &.pinned {
          .pin-answer {
            svg path[fill] {
              fill: var(--accent) !important;
              stroke: var(--accent) !important;
            }
          }
        }
      }
    }

    .submit-answer {
      margin-left: var(--spacing-24);
      padding-bottom: 200px;

      &.disabled {
        pointer-events: none;
        color: var(--true-black);
        opacity: 0.5;
      }

      h2 {
        font-weight: 500;
        margin: 0 0 10px 0;
      }

      .custom-input-group {
        padding-bottom: var(--spacing-16);
        position: relative;

        .qa-editor {
          margin-bottom: var(--spacing-16);

          &.invalid {
            border-color: var(--main-red);
          }
        }

        label:not(.codeblock-select-icon) {
          font-weight: bold;
          font-size: 14px;
          line-height: 14px;
          color: var(--main-dark-shade-0);
        }

        .lms-access {
          padding: 7px 24px;
          line-height: 150%;
          border-radius: 3px;
        }

        .tooltip {
          display: none;
          opacity: 0.8;
          filter: drop-shadow(0 4px 15px rgba(0, 0, 0, 0.15));
          background: var(--true-black);
          border-radius: 3px;
          font-weight: normal;
          font-size: 14px;
          line-height: 21px;
          color: var(--true-white);
          padding: 14px;
          position: absolute;
          top: 50%;
          margin-top: -70px;
          margin-left: 2px;
          left: 100%;
          width: 367px;

          & > * {
            display: block;
          }
        }

        .btn-holder {
          display: flex;
          flex-direction: row-reverse;

          .btn-cancel {
            margin-right: var(--spacing-24);
            color: var(--main-light-shade-4);

            &:hover {
              color: var(--main-dark-light-4);
            }

            &:active {
              color: var(--main-dark-light-3);
            }
          }
        }
      }
    }

    #modal {
      .modal-container {
        box-shadow: 0 16px 30px rgba(0, 12, 31, 0.15);
        border-radius: 20px;
        padding: var(--spacing-30) var(--spacing-44);
      }

      .modal-header {
        h1 {
          font-weight: 600;
          font-size: 28.43px !important;
          line-height: 34px;
          color: var(--main-dark-shade-0) !important;
          margin: 0 !important;
          text-align-last: center;
          width: 100%;
        }

        svg {
          position: relative;
          height: 16px;
          right: -24px;
          top: -21px;
          transform: unset;

          path[stroke] {
            stroke: var(--main-light-shade-4);
          }
        }
      }

      .modal-body {
        .custom-input-group {
          margin-bottom: 16px;
          position: relative;

          ul {
            list-style: none;
            padding: 0;
            margin: 0;

            li {
              font-weight: 500;
              font-size: 18px;
              line-height: 24px;
              color: var(--main-dark-shade-0);
              padding: var(--spacing-8);
              padding-left: var(--spacing-4);
              margin-bottom: var(--spacing-8);

              .custom-radio {
                span {
                  align-items: flex-start;

                  &:before {
                    margin-top: 5px;
                  }

                  &:after {
                    top: 8px;
                  }
                }
              }
            }
          }

          textarea {
            border-radius: 10px;

            &:focus {
              border-color: var(--accent);
            }

            &.error {
              border-color: var(--main-red);
            }
          }

          .why-report {
            font-weight: 400;
            font-size: 16px;
            line-height: 27px;
            color: var(--main-dark-light-4);
          }

          .btn-cancel {
            background: var(--brand-lighter) !important;
            color: var(--brand) !important;
            border: 0 !important;
          }

          .tooltip {
            display: none;
            opacity: 0.8;
            filter: drop-shadow(0 4px 15px rgba(0, 0, 0, 0.15));
            background: var(--true-black);
            border-radius: 3px;
            font-weight: normal;
            font-size: 14px;
            line-height: 21px;
            color: var(--true-white);
            padding: 14px;
            position: absolute;
            top: 50%;
            margin-top: -70px;
            margin-left: 2px;
            left: 100%;
            width: 367px;

            & > * {
              display: block;
            }
          }
        }
      }

      .modal-footer {
        justify-content: space-between;
        margin-top: var(--spacing-32);
        padding: 0;
      }
    }
  }

  .similar-questions {
    height: fit-content;
  }
}

@include bp($bp-laptop) {
  .question-wrapper {
    .details .submit-answer .custom-input-group .btn-holder .btn-primary {
      font-size: 16px;
      width: 166px;
      height: 40px;
      padding: 0;
    }
  }
}

@include bp($bp-mobile) {
  .question-wrapper {
    .details {
      .container-head {
        .back-button svg {
          height: 17px;
          width: 11px;
        }

        .question-actions {
          .follow,
          .mark-resolved,
          .unassign,
          .share {
            font-size: 0;
            margin-right: var(--spacing-40);

            svg {
              margin-right: 0;
            }
          }

          .share {
            margin-right: var(--spacing-28);
          }
        }
      }

      .question-info {
        margin-top: var(--spacing-24);
        margin-bottom: var(--spacing-12);
        align-items: flex-end;

        div.left {
          align-items: flex-start;
          flex-direction: column-reverse;

          .badge {
            margin-bottom: var(--spacing-8);
            margin-left: 38px;
          }
        }
      }

      .question-title {
        font-size: 16px;
      }

      .question-path {
        margin: var(--spacing-12) 0 var(--spacing-12) 0;

        a {
          font-weight: 600;
        }
      }

      .question-body {
        margin-bottom: var(--spacing-12);
        font-size: 14px;

        * {
          font-size: 14px;
        }
      }

      .time {
        display: flex;
        align-items: center;
        color: var(--main-dark-light-4);
        font-size: 13px;

        p {
          margin: 0;
          margin-left: 4px;
          font-size: 13px;
        }
      }

      .question-answers {
        margin-top: 50px;

        .answer {
          padding: var(--spacing-26) var(--spacing-32);
          margin-left: 0;

          .answer-info {
            align-items: flex-end;

            div.left {
              align-items: flex-start;

              .badge {
                margin-bottom: var(--spacing-8);
                margin-left: 38px;
              }
            }
          }

          .answer-body p {
            font-size: 14px;
          }

          .pin-answer {
            left: 0;
            top: -5px;
          }

          .answer-footer {
            div,
            span,
            p {
              font-size: 13px;
            }

            .mark-as-helpful-tooltip {
              display: none;
            }

            .helpful {
              color: var(--accent);
            }

            .time {
              margin-bottom: 0;
              margin-right: auto;
              color: var(--main-light-shade-4);
              flex-direction: column;
              align-items: flex-start;

              p {
                margin: 0;
              }
            }
          }
        }
      }

      .submit-answer {
        .custom-input-group {
          .btn-holder {
            height: 40px;
            align-items: center;
          }
        }
      }
    }

    .similar-questions {
      margin: var(--spacing-80) 0 0 0;
    }
  }
}

@include bp($bp-phone) {
  .question-wrapper {
    .details {
      .submit-answer {
        margin-left: 0;
        padding-bottom: 40px;

        button {
          width: 100%;
          height: 52px;
          padding: 0;
        }

        .custom-input-group {
          .btn-holder {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            height: unset;

            .btn-cancel {
              margin-right: 0;
              margin-top: var(--spacing-8);
            }
          }
        }
      }

      #modal {
        .modal-mask {
          top: 27px;
        }

        .modal-container {
          width: 80%;
          padding: 38px 35px var(--spacing-40) 35px;

          .modal-footer {
            flex-direction: column-reverse;
            align-items: stretch;

            button {
              &:last-of-type {
                margin-bottom: 8px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
